import { CardCvcElement, CardExpiryElement, CardNumberElement, useElements, useStripe } from '@stripe/react-stripe-js';
import { FormikHelpers, useFormik } from 'formik';
import get from 'lodash.get';
import React, { useContext } from 'react';
import { Col, Form, Row } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { object } from 'yup';

import apiUtil from 'src/api/payments';
import CustomToast from 'src/components/CustomToast';
import FormikSubmit from 'src/components/FormikSubmit';
import { AppContext } from 'src/containers/Core/AppContext';
import { AppState } from 'src/containers/Core/AppReducer';
import { CARD_ERROR, CARD_SUCCESS } from 'src/containers/Core/Messages';
import { requiredSchema } from 'src/validationSchemas';

type SetupCardDetailsProps = {
  onSuccess: () => void;
};

type PaymentFormValues = {
  name: string;
  cardNumber: string;
  expiry: string;
  cvc: string;
};

const SetupCardDetails: React.FC<SetupCardDetailsProps> = ({ onSuccess }) => {
  const stripe = useStripe();
  const elements = useElements();

  const { store } = useContext(AppContext);
  const { businessOnboardingDetails }: AppState = store;
  const businessUuid = get(businessOnboardingDetails, 'business.uuid');

  const stripeFormattingOptions = {
    style: {
      base: {
        fontSize: '14px',
        color: '#424770',
        '::placeholder': {
          color: '#aab7c4',
          fontSize: '14px',
        },
      },
      invalid: {
        color: '#9e2146',
      },
    },
  };

  const handlePaymentSubmit = async (data: PaymentFormValues, actions: FormikHelpers<PaymentFormValues>) => {
    if (!stripe || !elements) {
      return;
    }

    if (errors.cardNumber || errors.cvc || errors.expiry) {
      return;
    }

    try {
      actions.setSubmitting(true);

      const cardNumberElement = elements.getElement(CardNumberElement);

      const res = await apiUtil.getCardSetupIntent(businessUuid);

      const { error, setupIntent } = await stripe.confirmCardSetup(res.setupIntentClientSecret, {
        payment_method: {
          card: cardNumberElement,
          billing_details: {
            name: data.name,
          },
        },
      });

      if (error) {
        return;
      }

      if (setupIntent) {
        await apiUtil.addCard(businessUuid, setupIntent.payment_method);

        toast(<CustomToast type="success" description={CARD_SUCCESS} />, { className: 'success' });
        actions.resetForm();
        onSuccess();
      }
    } catch (error) {
      toast(<CustomToast title="Add New Card" description={CARD_ERROR} />);
    } finally {
      actions.setSubmitting(false);
    }
  };

  const initialValues = {
    name: '',
    cardNumber: '',
    expiry: '',
    cvc: '',
  };

  const validationSchema = object({
    name: requiredSchema,
  });

  const formik = useFormik({
    initialValues,
    validationSchema,
    onSubmit: handlePaymentSubmit,
  });

  const { touched, errors, isSubmitting, values, handleSubmit, handleChange, setFieldError } = formik;

  // tslint:disable-next-line:no-any
  const handleStripeInputChange = (e: any, fieldName: string) => {
    if (e.error) {
      setFieldError(fieldName, e.error.message);
    } else if (e.complete) {
      setFieldError(fieldName, null);
    }
  };

  const isStripeField = (fieldName: string) => ['cardNumber', 'expiry', 'cvc'].includes(fieldName);
  const renderError = (fieldName: string) =>
    (isStripeField(fieldName) ? true : get(touched, fieldName)) && get(errors, fieldName) ? (
      <div className="text-danger medium ml-2">{get(errors, fieldName)}</div>
    ) : null;

  return (
    <Form className="add-funds-form w-50" noValidate onSubmit={handleSubmit}>
      <div>
        <div className="font-size-14 font-weight-bold mb-4">Enter Credit/Debit Card Information</div>
        <Row>
          <Col>
            <Form.Group className="mb-1" controlId="Name">
              <Form.Label className="font-size-14 mb-1 required">Name on Card</Form.Label>
              <Form.Control
                className="border rounded p-2 font-size-14"
                placeholder="Enter name"
                value={values.name}
                type="text"
                onChange={handleChange}
                isInvalid={touched.name && !!errors.name}
                autoComplete="off"
                name="name"
                autoFocus={true}
                maxLength={255}
                disabled={isSubmitting}
              />
              {renderError('name')}
            </Form.Group>
          </Col>
        </Row>
        <Row>
          <Col>
            <Form.Group className="mb-1" controlId="CardNumber">
              <Form.Label className="font-size-14 mb-1 required">Card Number</Form.Label>
              <CardNumberElement
                className="border rounded p-2"
                onChange={e => handleStripeInputChange(e, 'cardNumber')}
                options={stripeFormattingOptions}
              />
              {renderError('cardNumber')}
            </Form.Group>
          </Col>
        </Row>
        <Row>
          <Col md={6}>
            <Form.Group className="mb-1" controlId="Expiry">
              <Form.Label className="font-size-14 mb-1 required">Expiration Date</Form.Label>
              <CardExpiryElement
                className="border rounded p-2 font-size-14"
                onChange={e => handleStripeInputChange(e, 'expiry')}
                options={stripeFormattingOptions}
              />
              {renderError('expiry')}
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group className="mb-1" controlId="CVV">
              <Form.Label className="font-size-14 mb-1 required">CVC</Form.Label>
              <CardCvcElement
                className="border rounded p-2 font-size-14"
                onChange={e => handleStripeInputChange(e, 'cvc')}
                options={stripeFormattingOptions}
              />
              {renderError('cvc')}
            </Form.Group>
          </Col>
        </Row>
      </div>

      <FormikSubmit
        size="sm"
        className="font-weight-bold py-2 mt-2"
        disabled={!!errors.cardNumber || !!errors.cvc || !!errors.expiry}
        loading={isSubmitting}
        text="Add Card"
      />
    </Form>
  );
};

export default SetupCardDetails;
